import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { Button, Card, Input, Skeleton } from "../components/ui";
import Footer from "../components/Footer";
import { ArrowLeft, CheckCircle, Lock } from "lucide-react";

/** Opened from the link a club admin shared: set a new password. */
export default function ResetPasswordPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [account, setAccount] = useState(null); // null = checking
  const [invalid, setInvalid] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setInvalid("This link is missing its reset code."); return; }
    api.get("/auth/reset-password/check", { params: { token } })
      .then(({ data }) => setAccount(data))
      .catch((err) => setInvalid(apiError(err)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const Shell = ({ children }) => (
    <div className="app-shell flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 text-center">{children}</div>
      <Footer />
    </div>
  );

  if (invalid) {
    return (
      <Shell>
        <h1 className="text-2xl font-bold mb-2">Link not valid</h1>
        <p className="text-ink-muted text-[15px] mb-6">{invalid}</p>
        <Link to="/forgot-password" className="text-brand font-semibold text-sm">Request a new reset</Link>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="h-16 w-16 rounded-2xl bg-brand-100 grid place-items-center mb-4">
          <CheckCircle size={32} className="text-brand" />
        </div>
        <h1 className="text-2xl font-bold mb-2">Password updated!</h1>
        <p className="text-ink-muted text-[15px] mb-6">Sign in with {account?.email} and your new password.</p>
        <Button size="lg" className="w-full max-w-[300px]" onClick={() => nav("/login", { replace: true })} data-testid="reset-goto-login">
          Sign in
        </Button>
      </Shell>
    );
  }

  return (
    <div className="app-shell flex flex-col">
      <div className="flex-1">
        <div className="flex justify-center pt-12 pb-8">
          <img src="/logo.png" alt="Cricso" className="h-32 w-32 object-contain" />
        </div>
        <div className="px-6 pb-6 text-center">
          <h1 className="text-3xl font-bold leading-tight">
            Set a new <span className="text-brand">password</span>
          </h1>
          {account ? (
            <p className="text-ink-muted mt-2 text-[15px]">For {account.name} ({account.email})</p>
          ) : (
            <Skeleton className="h-5 w-48 mx-auto mt-2" />
          )}
        </div>
        <Card className="mx-5 mb-5">
          <form onSubmit={submit} className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-ink-muted">
              <Lock size={16} /> Choose something you'll remember
            </div>
            <Input label="New password" type="password" placeholder="At least 6 characters" value={password}
              onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="reset-password-input" />
            <Input label="Confirm password" type="password" placeholder="Type it again" value={confirm}
              onChange={(e) => setConfirm(e.target.value)} required minLength={6} data-testid="reset-confirm-input" />
            {password && confirm && password !== confirm && <p className="text-xs text-red-500">Passwords don't match</p>}
            <Button type="submit" className="w-full" size="lg" disabled={loading || !account || (confirm && password !== confirm)} data-testid="reset-submit-btn">
              {loading ? "Saving…" : "Save new password"}
            </Button>
          </form>
        </Card>
        <div className="text-center text-sm mt-2 pb-10">
          <Link to="/login" className="inline-flex items-center gap-1.5 text-brand font-semibold">
            <ArrowLeft size={16} /> Back to Sign in
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
